/**
 * Account-level Meta Conversions API token list manager.
 *
 * Replaces the single-token UX in MetaCapiSettings (mode="account").
 * The operator can now keep multiple Pixel + access-token pairs at the
 * account level (e.g. one per BM, one per pixel rotation, one per
 * advertiser) and pick which one is the default for new landing pages.
 *
 * Per-page overrides still live in Builder.tsx → MetaCapiSettings
 * mode="page" plus the new "use this account-token" picker; the data
 * model is documented in server/services/metaCapi.js.
 *
 * UX choices that mirror Stripe / Vercel API key managers:
 *   - Tokens show as masked rows (prefix + suffix + length badge)
 *   - "Reveal" never round-trips — the secret is server-only after save
 *   - Renames don't require re-pasting the EAA... value
 *   - "Send test event" lives inline per-token so the operator can
 *     verify a *specific* token without leaving the page
 */

import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Plus,
  Send,
  Star,
  Trash2,
  Eye,
  EyeOff,
  X,
  Pencil,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API_URL = 'https://api.hitzlink.com/api';

function getAuthToken(): string {
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  return localStorage.getItem('token') || '';
}

interface AccountToken {
  id: string;
  name: string;
  pixelId: string;
  testEventCode: string;
  createdAt: string | null;
  hasAccessToken: boolean;
  accessTokenMasked: string;
  accessTokenPrefix: string;
  accessTokenSuffix: string;
  accessTokenLength: number;
}

interface AccountCapiState {
  enabled: boolean;
  defaultTokenId: string | null;
  tokens: AccountToken[];
}

export const AccountCapiTokens: React.FC = () => {
  const { t } = useTranslation('tracking');
  const [state, setState] = React.useState<AccountCapiState>({
    enabled: false,
    defaultTokenId: null,
    tokens: [],
  });
  const [loading, setLoading] = React.useState(true);
  const [savingFlag, setSavingFlag] = React.useState(false);

  // Editor modal state — "create" or "edit existing"
  const [editing, setEditing] = React.useState<null | {
    mode: 'create' | 'edit';
    tokenId?: string;
    name: string;
    pixelId: string;
    accessToken: string;
    testEventCode: string;
    accessTokenMasked?: string;
  }>(null);

  async function reload() {
    try {
      const t = getAuthToken();
      const res = await fetch(`${API_URL}/auth/me/capi-settings`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setState({
        enabled: Boolean(data.enabled),
        defaultTokenId: data.defaultTokenId || null,
        tokens: Array.isArray(data.tokens) ? data.tokens : [],
      });
    } catch (err) {
      console.warn('[capi tokens] reload failed', err);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    reload();
  }, []);

  async function handleToggleEnabled(next: boolean) {
    setSavingFlag(true);
    try {
      const auth = getAuthToken();
      const res = await fetch(`${API_URL}/auth/me/capi-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error('failed');
      setState((s) => ({ ...s, enabled: next }));
      toast.success(next ? t('capi.toast.enabled') : t('capi.toast.disabled'));
    } catch (err) {
      toast.error(t('capi.toast.updateFailed'));
    } finally {
      setSavingFlag(false);
    }
  }

  async function handleMakeDefault(id: string) {
    try {
      const auth = getAuthToken();
      const res = await fetch(`${API_URL}/auth/me/capi-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
        body: JSON.stringify({ defaultTokenId: id }),
      });
      if (!res.ok) throw new Error('failed');
      setState((s) => ({ ...s, defaultTokenId: id }));
      toast.success(t('capi.toast.defaultUpdated'));
    } catch (err) {
      toast.error(t('capi.toast.defaultFailed'));
    }
  }

  async function handleDelete(id: string) {
    const tok = state.tokens.find((x) => x.id === id);
    if (!tok) return;
    if (!confirm(t('capi.toast.removeConfirm', { name: tok.name }))) return;
    try {
      const auth = getAuthToken();
      const res = await fetch(`${API_URL}/auth/me/capi-settings/tokens/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth}` },
      });
      if (!res.ok) throw new Error('failed');
      await reload();
      toast.success(t('capi.toast.removed'));
    } catch (err) {
      toast.error(t('capi.toast.removeFailed'));
    }
  }

  function openCreate() {
    setEditing({
      mode: 'create',
      name: '',
      pixelId: '',
      accessToken: '',
      testEventCode: '',
    });
  }

  function openEdit(tok: AccountToken) {
    setEditing({
      mode: 'edit',
      tokenId: tok.id,
      name: tok.name,
      pixelId: tok.pixelId,
      accessToken: '',
      testEventCode: tok.testEventCode,
      accessTokenMasked: tok.accessTokenMasked || '',
    });
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-emerald-600" />
        <h4 className="text-base font-semibold text-gray-900">
          {t('capi.heading')}
        </h4>
        {state.enabled && state.tokens.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-500/15 text-emerald-600">
            <CheckCircle2 className="w-3 h-3" /> {t('capi.active')}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600">{t('capi.description')}</p>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('capi.loading')}
        </div>
      ) : (
        <>
          {/* Master enable toggle */}
          <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-gray-900">
                {t('capi.master.title')}
              </div>
              <div className="text-xs text-gray-500">
                {t('capi.master.subtitle')}
              </div>
            </div>
            <button
              type="button"
              disabled={savingFlag || state.tokens.length === 0}
              onClick={() => handleToggleEnabled(!state.enabled)}
              className={
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ' +
                (state.enabled ? 'bg-emerald-500' : 'bg-gray-300')
              }
              aria-pressed={state.enabled}
            >
              <span
                className={
                  'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
                  (state.enabled ? 'translate-x-5' : 'translate-x-1')
                }
              />
            </button>
          </div>

          {/* Token list */}
          {state.tokens.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
              <Activity className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <h5 className="text-sm font-semibold text-gray-700 mb-1">
                {t('capi.empty.title')}
              </h5>
              <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                {t('capi.empty.hint')}
              </p>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> {t('capi.empty.addButton')}
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {state.tokens.map((tok) => (
                  <TokenRow
                    key={tok.id}
                    token={tok}
                    isDefault={state.defaultTokenId === tok.id}
                    onMakeDefault={() => handleMakeDefault(tok.id)}
                    onDelete={() => handleDelete(tok.id)}
                    onEdit={() => openEdit(tok)}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> {t('capi.addAnother')}
              </button>
            </>
          )}
        </>
      )}

      {editing && (
        <TokenEditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 *  Single-token row
 * ──────────────────────────────────────────────────────────────────── */

const TokenRow: React.FC<{
  token: AccountToken;
  isDefault: boolean;
  onMakeDefault: () => void;
  onDelete: () => void;
  onEdit: () => void;
}> = ({ token, isDefault, onMakeDefault, onDelete, onEdit }) => {
  const { t } = useTranslation('tracking');

  return (
    <div
      className={
        'rounded-lg border bg-white p-4 transition-colors ' +
        (isDefault ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200')
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {token.name || t('capi.row.unnamed')}
            </span>
            {isDefault && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-emerald-500/15 text-emerald-600 font-medium">
                <Star className="w-3 h-3 fill-current" /> {t('capi.row.default')}
              </span>
            )}
            {token.pixelId && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-mono">
                {t('capi.row.pixelLabel', { id: token.pixelId })}
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="font-mono text-gray-700">{token.accessTokenMasked || '••••'}</span>
            <span className="text-gray-400">·</span>
            <span>{t('capi.row.chars', { count: token.accessTokenLength })}</span>
            {token.testEventCode && (
              <>
                <span className="text-gray-400">·</span>
                <span>
                  {t('capi.row.testCodeLabel')} <span className="font-mono">{token.testEventCode}</span>
                </span>
              </>
            )}
            {token.accessTokenLength > 0 && token.accessTokenLength < 150 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                <AlertCircle className="w-3 h-3" /> {t('capi.row.looksShort')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isDefault && (
            <button
              type="button"
              onClick={onMakeDefault}
              className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
              title={t('capi.row.makeDefault')}
            >
              <Star className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title={t('capi.row.edit')}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title={t('capi.row.remove')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 *  Add / edit token modal
 * ──────────────────────────────────────────────────────────────────── */

interface EditorState {
  mode: 'create' | 'edit';
  tokenId?: string;
  name: string;
  pixelId: string;
  accessToken: string;
  testEventCode: string;
  /** Masked token display for edit mode (e.g. "EAAr••••ZDZD") */
  accessTokenMasked?: string;
}

const TokenEditorModal: React.FC<{
  initial: EditorState;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}> = ({ initial, onClose, onSaved }) => {
  const { t } = useTranslation('tracking');
  const [name, setName] = React.useState(initial.name);
  const [pixelId, setPixelId] = React.useState(initial.pixelId);
  const [accessToken, setAccessToken] = React.useState(initial.accessToken);
  const [testEventCode, setTestEventCode] = React.useState(initial.testEventCode);
  const [makeDefault, setMakeDefault] = React.useState(initial.mode === 'create');
  const [showToken, setShowToken] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<null | { ok: boolean; msg: string }>(null);
  const [replacingToken, setReplacingToken] = React.useState(false);

  const isEdit = initial.mode === 'edit';
  const hasExistingToken = isEdit && Boolean(initial.accessTokenMasked);

  async function handleSave() {
    setSaving(true);
    try {
      const auth = getAuthToken();
      const body: Record<string, unknown> = {
        name: name.trim(),
        pixelId: pixelId.trim(),
        testEventCode: testEventCode.trim(),
        makeDefault,
      };
      if (accessToken.trim()) {
        body.accessToken = accessToken.trim();
      } else if (!isEdit) {
        toast.error(t('capi.editor.toast.needToken'));
        setSaving(false);
        return;
      }
      const url = isEdit
        ? `${API_URL}/auth/me/capi-settings/tokens/${encodeURIComponent(initial.tokenId!)}`
        : `${API_URL}/auth/me/capi-settings/tokens`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'save failed');
      toast.success(isEdit ? t('capi.editor.toast.saved') : t('capi.editor.toast.added'));
      await onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('capi.editor.toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTestResult(null);
    setTesting(true);
    try {
      const useNewToken = accessToken.trim();
      if (!useNewToken && !hasExistingToken) {
        throw new Error(t('capi.editor.toast.needToken'));
      }
      if (!pixelId.trim()) {
        throw new Error(t('capi.editor.toast.needPixel'));
      }
      if (!testEventCode.trim()) {
        throw new Error(t('capi.editor.toast.needTestCode'));
      }
      const auth = getAuthToken();
      const body: Record<string, string> = {
        pixelId: pixelId.trim(),
        testEventCode: testEventCode.trim(),
      };
      if (useNewToken) {
        body.accessToken = useNewToken;
      } else if (initial.tokenId) {
        body.tokenId = initial.tokenId;
      }
      const res = await fetch(`${API_URL}/capi/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('capi.editor.toast.metaRejected'));
      setTestResult({
        ok: true,
        msg: t('capi.editor.toast.testSent', { id: data.fbtrace_id || '—' }),
      });
    } catch (err) {
      setTestResult({
        ok: false,
        msg: err instanceof Error ? err.message : t('capi.editor.toast.testFailed'),
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? t('capi.editor.titleEdit') : t('capi.editor.titleAdd')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            aria-label={t('capi.editor.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('capi.editor.labelHeading')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('capi.editor.labelPlaceholder')}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={80}
            />
            <p className="text-xs text-gray-500 mt-1">{t('capi.editor.labelHint')}</p>
          </div>

          {/* Pixel ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('capi.editor.pixelLabel')}{' '}
              <span className="text-gray-400 font-normal">{t('capi.editor.pixelOptional')}</span>
            </label>
            <input
              type="text"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder={t('capi.editor.pixelPlaceholder')}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              maxLength={40}
            />
            <p className="text-xs text-gray-500 mt-1">{t('capi.editor.pixelHint')}</p>
          </div>

          {/* Access token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('capi.editor.tokenLabel')}
            </label>
            {hasExistingToken && !replacingToken ? (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={initial.accessTokenMasked}
                    disabled
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 font-mono opacity-70"
                  />
                  <button
                    type="button"
                    onClick={() => { setReplacingToken(true); setAccessToken(''); }}
                    className="px-3 py-2 text-sm rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                  >
                    {t('capi.editor.tokenReplace')}
                  </button>
                </div>
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  {t('capi.editor.tokenKept')}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    placeholder={t('capi.editor.tokenPlaceholder')}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken((s) => !s)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                    aria-label={showToken ? t('capi.editor.tokenHide') : t('capi.editor.tokenShow')}
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  {hasExistingToken && (
                    <button
                      type="button"
                      onClick={() => { setReplacingToken(false); setAccessToken(''); }}
                      className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 whitespace-nowrap"
                    >
                      {t('capi.editor.tokenCancel')}
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('capi.editor.tokenHint')}</p>
              </>
            )}
          </div>

          {/* Test event code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('capi.editor.testCodeLabel')}{' '}
              <span className="text-gray-400 font-normal">{t('capi.editor.testCodeOptional')}</span>
            </label>
            <input
              type="text"
              value={testEventCode}
              onChange={(e) => setTestEventCode(e.target.value)}
              placeholder={t('capi.editor.testCodePlaceholder')}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">{t('capi.editor.testCodeHint')}</p>
          </div>

          {/* Make default */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">
              {t('capi.editor.makeDefault')}
              <span className="block text-xs text-gray-500">
                {t('capi.editor.makeDefaultHint')}
              </span>
            </span>
          </label>

          {/* Test result */}
          {testResult && (
            <div
              className={
                (testResult.ok
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border-red-200 text-red-700') +
                ' border rounded-lg p-3 text-sm flex gap-2'
              }
            >
              {testResult.ok ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              )}
              <span>{testResult.msg}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || (!accessToken.trim() && !hasExistingToken) || !pixelId.trim() || !testEventCode.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('capi.editor.sendTest')}
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm font-medium"
          >
            {t('capi.editor.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || (!isEdit && !accessToken.trim())}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isEdit ? t('capi.editor.save') : t('capi.editor.add')}
          </button>
        </div>
      </div>
    </div>
  );
};
